import Dialog from "~/ui-rtk/components/ui/common/Dialog"
import SourcePreview from "./SourcePreview"
import { useBlastWizard } from "./useBlastWizard"
import type { BlastSource } from "./types"
import DestinationSection from "../details/AdDetails/components/DestinationSection"
import CreativeOverridesSection from "../details/AdDetails/components/CreativeOverridesSection"
import ActionFooter from "../details/AdDetails/components/ActionFooter"
import FiltersModal from "../details/AdDetails/components/FiltersModal"

interface BlastWizardProps {
  isOpen: boolean
  onClose: () => void
  source: BlastSource
  isDemoEnabled?: boolean
}

export default function BlastWizard({
  isOpen,
  onClose,
  source,
  isDemoEnabled = false,
}: BlastWizardProps) {
  const wizard = useBlastWizard(source, isOpen, onClose)

  return (
    <>
      <Dialog
        isOpen={isOpen}
        handleClose={onClose}
        width={800}
        classes={{
          container:
            "bg-system-blue-800 border-2 border-system-blue-700 rounded-2xl p-0 relative flex flex-col max-w-2xl w-full max-h-[90vh] overflow-hidden",
        }}
      >
        <div className="flex flex-col h-full overflow-hidden">
          {/* Header with title and close button */}
          <div className="flex-shrink-0 p-6 pb-0">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-bold text-white text-left text-xl">
                Ad Forge
              </h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path
                    d="M18 6L6 18M6 6l12 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>

            {/* Source preview - always visible */}
            <SourcePreview source={source} isDemoEnabled={isDemoEnabled} />
          </div>

          <CreativeOverridesSection
            overrides={wizard.creativeOverrides}
            onOverridesChange={wizard.setCreativeOverrides}
            disabled={wizard.isBlasting}
            requiredFields={[]}
          />

          <DestinationSection
            campaignSearchQuery={wizard.campaignSearchQuery}
            onCampaignSearchChange={wizard.setCampaignSearchQuery}
            onFiltersClick={() => wizard.setIsFiltersModalOpen(true)}
            isLoadingAdSets={wizard.isLoadingAdSets}
            filters={wizard.filters}
            filteredAdSets={wizard.filteredAdSets}
            selectedAdSets={wizard.selectedAdSets}
            onToggleAdSet={wizard.toggleAdSet}
            onShowCampaign={campaignId =>
              wizard.setFilters(prev => ({
                ...prev,
                campaignFilter: campaignId,
              }))
            }
            isLoadingMore={wizard.isLoadingMore}
            hasMore={wizard.hasMore}
            onScroll={wizard.handleScroll}
            scrollContainerRef={wizard.scrollContainerRef}
          />

          <ActionFooter
            onCancel={onClose}
            onBlastAds={wizard.handleBlast}
            isBlasting={wizard.isBlasting}
            summaryStats={wizard.summaryStats}
          />
        </div>
      </Dialog>

      {/* Filters Modal */}
      <FiltersModal
        isOpen={wizard.isFiltersModalOpen}
        onClose={() => wizard.setIsFiltersModalOpen(false)}
        campaigns={wizard.allCampaigns}
        filters={wizard.filters}
        onFiltersChange={wizard.setFilters}
      />
    </>
  )
}
